export const RAPID_SWITCH_DEFAULTS = Object.freeze({
  snVinWindowSeconds: 30,
  snVinNorthAmericaThreshold: 9,
  snVinOtherThreshold: 4,
  vinSnWindowSeconds: 60,
  vinSnNorthAmericaThreshold: 9,
  vinSnOtherThreshold: 4,
});

export const RAPID_SWITCH_LIMITS = Object.freeze({
  windowSeconds: Object.freeze({
    min: 1,
    max: 3600,
  }),
  threshold: Object.freeze({
    min: 2,
    max: 1000,
  }),
});

const BUSINESS_FIELDS = Object.freeze([
  'snVinWindowSeconds',
  'snVinNorthAmericaThreshold',
  'snVinOtherThreshold',
  'vinSnWindowSeconds',
  'vinSnNorthAmericaThreshold',
  'vinSnOtherThreshold',
]);

const FIELD_RULES = Object.freeze({
  snVinWindowSeconds: {
    label: '同一 SN 切换 VIN 窗口',
    min: RAPID_SWITCH_LIMITS.windowSeconds.min,
    max: RAPID_SWITCH_LIMITS.windowSeconds.max,
  },
  snVinNorthAmericaThreshold: {
    label: '同一 SN 切换 VIN：北美阈值',
    min: RAPID_SWITCH_LIMITS.threshold.min,
    max: RAPID_SWITCH_LIMITS.threshold.max,
  },
  snVinOtherThreshold: {
    label: '同一 SN 切换 VIN：非北美阈值',
    min: RAPID_SWITCH_LIMITS.threshold.min,
    max: RAPID_SWITCH_LIMITS.threshold.max,
  },
  vinSnWindowSeconds: {
    label: '同一 VIN 被多个 SN 访问窗口',
    min: RAPID_SWITCH_LIMITS.windowSeconds.min,
    max: RAPID_SWITCH_LIMITS.windowSeconds.max,
  },
  vinSnNorthAmericaThreshold: {
    label: '同一 VIN 被多个 SN 访问：北美阈值',
    min: RAPID_SWITCH_LIMITS.threshold.min,
    max: RAPID_SWITCH_LIMITS.threshold.max,
  },
  vinSnOtherThreshold: {
    label: '同一 VIN 被多个 SN 访问：非北美阈值',
    min: RAPID_SWITCH_LIMITS.threshold.min,
    max: RAPID_SWITCH_LIMITS.threshold.max,
  },
});

function normalizeInteger(value, fallback, min, max) {
  const normalized = typeof value === 'number'
    ? value
    : Number(String(value ?? '').trim());

  if (!Number.isInteger(normalized) || normalized < min || normalized > max) {
    return fallback;
  }
  return normalized;
}

function normalizeThresholdPair(config, fallbacks, northAmericaField, otherField) {
  if (config[northAmericaField] < config[otherField]) {
    config[northAmericaField] = fallbacks[northAmericaField];
    config[otherField] = fallbacks[otherField];
  }
}

function normalizeBusinessConfig(payload, fallbacks) {
  const config = BUSINESS_FIELDS.reduce((normalized, field) => {
    const rule = FIELD_RULES[field];
    normalized[field] = normalizeInteger(
      payload?.[field],
      fallbacks[field],
      rule.min,
      rule.max
    );
    return normalized;
  }, {});

  normalizeThresholdPair(
    config,
    fallbacks,
    'snVinNorthAmericaThreshold',
    'snVinOtherThreshold'
  );
  normalizeThresholdPair(
    config,
    fallbacks,
    'vinSnNorthAmericaThreshold',
    'vinSnOtherThreshold'
  );

  return config;
}

function normalizeEnabled(value) {
  if (typeof value === 'boolean') {
    return value;
  }
  return ['1', 'true', 'on'].includes(String(value ?? '').trim().toLowerCase());
}

export function normalizeRiskControlPayload(payload) {
  const body = payload?.data && typeof payload.data === 'object'
    ? payload.data
    : (payload || {});
  const defaults = normalizeBusinessConfig(
    body.defaults,
    RAPID_SWITCH_DEFAULTS
  );

  return {
    enabled: normalizeEnabled(body.enabled),
    ...normalizeBusinessConfig(body, RAPID_SWITCH_DEFAULTS),
    defaults,
  };
}

export function validateRiskControlDraft(draft) {
  for (const field of BUSINESS_FIELDS) {
    const rule = FIELD_RULES[field];
    const value = draft?.[field];

    if (!Number.isInteger(value)) {
      return `${rule.label}必须是整数`;
    }
    if (value < rule.min || value > rule.max) {
      return `${rule.label}必须在 ${rule.min} 到 ${rule.max} 之间`;
    }
  }

  if (draft.snVinNorthAmericaThreshold < draft.snVinOtherThreshold) {
    return '同一 SN 切换 VIN：北美阈值不得小于非北美阈值';
  }
  if (draft.vinSnNorthAmericaThreshold < draft.vinSnOtherThreshold) {
    return '同一 VIN 被多个 SN 访问：北美阈值不得小于非北美阈值';
  }

  return '';
}

export function isRiskierChange(before, after) {
  return (
    after?.snVinNorthAmericaThreshold < before?.snVinNorthAmericaThreshold ||
    after?.snVinOtherThreshold < before?.snVinOtherThreshold ||
    after?.vinSnNorthAmericaThreshold < before?.vinSnNorthAmericaThreshold ||
    after?.vinSnOtherThreshold < before?.vinSnOtherThreshold
  );
}

export function toRiskControlRequest(draft) {
  return BUSINESS_FIELDS.reduce((request, field) => {
    request[field] = draft[field];
    return request;
  }, {});
}
